import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import classNames from 'classnames'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { FormCheckbox } from '../FormFields/FormCheckbox'
import { FormRadioGroup } from '../FormFields/FormRadioGroup'
import { LinkText } from '../Atoms/Buttons'
import Disclosure from '../Atoms/Disclosure'

export interface MultipleRadioOption<T> {
  name: string
  value: T
  checked: boolean
  disabled?: boolean
}

export interface MixedFilter<T> {
  title: string
  isMultiple: boolean
  selected?: T
  simpleOptions?: { name: string; value: T }[]
  multipleOptions?: MultipleRadioOption<T>[]
  disabled?: boolean
  onSelectSimpleOption?: (opt: T) => void
  onSelectMultipleOption?: Dispatch<SetStateAction<MultipleRadioOption<T>[]>>
}

interface MixedFilterDisclosureProps {
  title: string
  filters: MixedFilter<any>[]
  showCount?: boolean
  showClear?: boolean
  className?: string
}

export const MixedFilterDisclosure = ({
  title,
  filters,
  showCount,
  showClear,
  className,
}: MixedFilterDisclosureProps) => {
  const [open, setOpen] = useState(false)

  const count = useMemo(() => {
    return filters
      .filter((f) => f.isMultiple)
      .reduce((acc, f) => {
        const current = f.multipleOptions?.reduce(
          (optionAcc, opt) => optionAcc + Number(!!opt.checked),
          0
        )
        return acc + (current ?? 0)
      }, 0)
  }, [filters])

  const columnCount = useMemo(() => {
    if (filters.length === 0) return 1
    return Math.max(1, Math.ceil(filters.length / 2))
  }, [filters.length])

  const gridTemplateColumns = useMemo(() => {
    return `repeat(${columnCount}, minmax(0, 1fr))`
  }, [columnCount])

  const toggleMultipleOption = (filterIndex: number, optionIndex: number) => {
    const filter = filters[filterIndex]
    if (!filter || !filter.onSelectMultipleOption) return

    filter.onSelectMultipleOption((prev) =>
      prev.map((opt, idx) => {
        if (idx === optionIndex) {
          return { ...opt, checked: !opt.checked }
        }
        return opt
      })
    )
  }

  const clear = () => {
    filters.forEach((f) => {
      if (f.isMultiple && f.onSelectMultipleOption && f.multipleOptions) {
        f.onSelectMultipleOption(
          f.multipleOptions.map((opt) => ({ ...opt, checked: false }))
        )
      } else if (
        !f.isMultiple &&
        f.onSelectSimpleOption &&
        f.simpleOptions?.length
      ) {
        f.onSelectSimpleOption(f.simpleOptions[0].value)
      }
    })
  }

  return (
    <div>
      <h3 className="flow-root">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={classNames(
            'flex w-full items-center justify-between px-2 py-3 text-sm text-gray-400',
            className
          )}
        >
          <span className="font-medium text-gray-900">{title}</span>

          {showCount && (
            <span className="ml-1.5 rounded bg-indigo-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
              {count}
            </span>
          )}

          <span className="ml-2 flex items-center">
            <ChevronDownIcon
              className={classNames(
                open ? '-rotate-180' : 'rotate-0',
                'h-5 w-5 transform'
              )}
              aria-hidden="true"
            />
          </span>
        </button>
      </h3>

      <Disclosure open={open} setOpen={setOpen} className="w-full sm:w-auto">
        <div
          className="grid p-4 gap-4 pt-2"
          style={{
            gridTemplateColumns: '1fr '.repeat(filters.length / 2),
          }}
        >
          {filters.map((f, index) => (
            <div
              key={`${f.title}-${index}`}
              className={classNames(
                'min-w-min',
                index > 1 && 'border-t border-gray-300'
              )}
            >
              <div className="min-w-min pt-2">
                <div className="flex flex-1 py-1 mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {f.title}
                  </span>
                </div>

                <div className="pl-1">
                  {f.isMultiple ? (
                    <div className="flex flex-col gap-2">
                      {f.multipleOptions?.map((opt, optIndex) => (
                        <FormCheckbox
                          key={`${f.title}-${opt.name}-${optIndex}`}
                          name={`filter-${f.title}-${optIndex}`}
                          label={opt.name}
                          checked={!!opt.checked}
                          disabled={f.disabled || opt.disabled}
                          onChange={() => {
                            if (f.disabled || opt.disabled) return
                            toggleMultipleOption(index, optIndex)
                          }}
                          labelClassname="text-xs text-gray-900"
                          containerClassname="gap-2"
                        />
                      ))}
                    </div>
                  ) : (
                    <FormRadioGroup
                      name={`filter-${f.title}-${index}`}
                      options={f.simpleOptions ?? []}
                      selected={f.selected}
                      disabled={f.disabled}
                      onSelectOption={(value) =>
                        f.onSelectSimpleOption?.(value)
                      }
                      optionsClassName="text-xs text-gray-900"
                      wrapperClassName="!gap-2"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={classNames(
            'flex flex-1 items-center justify-center py-3',
            !showClear && 'hidden'
          )}
        >
          <LinkText text="Limpiar" className="text-sm" onClick={clear} />
        </div>
      </Disclosure>
    </div>
  )
}
